import { Alert, AlertDescription, Button, Text, View } from "@voicerun/design-system";
import { useLocation, useNavigate } from "react-router-dom";
import * as monaco from "monaco-editor";
import clsx from "clsx";
import Editor from "@monaco-editor/react";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

import { agentFunctionActions } from "../../redux/reducers/agentFunctionSlice";
import { ConfirmDialog } from "../ConfirmDialog";
import { Dialog, FileField, FolderUploadField, InputField } from "../base";
import { featureActions } from "../../redux/reducers/featureSlice";
import { Icon } from "../base/Icon";
import { makeRequest } from "../../utils/request";
import { python } from "../../utils/editor";
import { PYTHON_FUNCTION_TEMPLATE, JAVASCRIPT_FUNCTION_TEMPLATE } from "../../pages/agent/AgentFunctionPage";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useTheme } from "../../contexts/ThemeContext";
import { zipFiles, unzipCode } from "../../utils/files";

const shouldIgnoreFile = (filePath: string): boolean => {
  const ignorePatterns = [
    // Virtual environments
    ".venv",
    "venv",
    "env",
    "ENV",
    "env.bak",
    "venv.bak",

    // Python cache and compiled files
    "__pycache__",
    ".pyc",
    ".pyo",
    ".pyd",
    ".pyo",
    ".pyd",

    // System files
    ".DS_Store",
    "Thumbs.db",
    "desktop.ini",
    ".Spotlight-V100",
    ".Trashes",
    "ehthumbs.db",

    // Git
    ".git",
    ".gitignore",
    ".gitattributes",
    ".gitmodules",

    // Package managers
    "uv.lock",
    "package-lock.json",
    "yarn.lock",
    "pnpm-lock.yaml",
    "poetry.lock",
    "Pipfile.lock",

    // Archives
    "*.zip",
    "*.tar.gz",
    "*.rar",
    "*.7z",

    // IDE and editor files
    ".vscode",
    ".idea",
    "*.swp",
    "*.swo",
    "*~",
    ".sublime-project",
    ".sublime-workspace",

    // Logs and temporary files
    "*.log",
    "*.tmp",
    "*.temp",
    ".tmp",
    ".temp",

    // Node.js
    "node_modules",
    "npm-debug.log*",
    "yarn-debug.log*",
    "yarn-error.log*",

    // Build and distribution
    "dist",
    "build",
    "*.egg-info",
    "__pycache__",
    ".pytest_cache",
    ".coverage",
    "htmlcov",

    // Environment files
    ".env",
    ".env.local",
    ".env.development",
    ".env.test",
    ".env.production",
  ];

  const pathParts = filePath.toLowerCase().split("/");
  for (const part of pathParts) {
    for (const pattern of ignorePatterns) {
      // Handle wildcard patterns
      if (pattern.includes("*")) {
        const regexPattern = pattern.replace(/\*/g, ".*");
        if (new RegExp(regexPattern).test(part)) {
          return true;
        }
      } else if (part === pattern.toLowerCase()) {
        return true;
      }
    }
  }
  return false;
};

export interface FilesState {
  [filename: string]: {
    content: string;
    language: string;
  };
}

type AgentFunctionEditorAdvancedProps = {
  code?: string;
  isMultifile?: boolean;
  files?: FilesState;
  activeFile?: string;
  onChange: (files: FilesState) => void;
  onActiveFileChange?: (filename: string) => void;
  readOnly?: boolean;
  readOnlyMessage?: string;
  language?: string;
  functionId?: string;
  latestNonDeployedVersionName?: string;
};

export const AgentFunctionEditorAdvanced = ({
  code: propCode,
  isMultifile: propIsZip,
  files: propFiles,
  activeFile: propActiveFile,
  onChange,
  onActiveFileChange,
  readOnly = false,
  readOnlyMessage,
  language = "python",
  functionId,
  latestNonDeployedVersionName,
}: AgentFunctionEditorAdvancedProps) => {
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const agentFunctionFormData = useAppSelector((state) => state.feature.agentFunctionFormData);

  // Add CSS for diff highlighting
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .diff-removed-line {
        background-color: #ffebee !important;
        text-decoration: line-through;
        opacity: 0.7;
      }
      .diff-added-line {
        background-color: #e8f5e8 !important;
        font-weight: 500;
      }
      .diff-removed-marker::before {
        content: "-";
        color: #d32f2f;
        font-weight: bold;
        margin-right: 8px;
      }
      .diff-added-marker::before {
        content: "+";
        color: #388e3c;
        font-weight: bold;
        margin-right: 8px;
      }
      [data-theme="dark"] .diff-removed-line {
        background-color: #3d1f1f !important;
      }
      [data-theme="dark"] .diff-added-line {
        background-color: #1f3d1f !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const isInitializedRef = useRef(false);
  const lastProcessedCodeRef = useRef<string | undefined>();
  const prevFunctionIdRef = useRef<string | undefined>();

  // State for UI
  const [showEditor, setShowEditor] = useState(true);
  const [showFileExplorer, setShowFileExplorer] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState<"files" | "content">("content");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(192); // 192px = w-48 default
  const [isResizing, setIsResizing] = useState(false);
  const [expandedSearchFiles, setExpandedSearchFiles] = useState<Set<string>>(new Set());
  const [fontSize, setFontSize] = useState(14);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<string>("");
  const [focusedNode, setFocusedNode] = useState<string>("");
  const [activeFolder, setActiveFolder] = useState<string | null>(null); // Track which folder is active for new files
  const [openTabs, setOpenTabs] = useState<string[]>([]); // Track which files are open in tabs
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    targetFile: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    targetFile: "",
  });
  const [renamingFile, setRenamingFile] = useState<string>("");
  const [renameValue, setRenameValue] = useState<string>("");
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [tabDropTarget, setTabDropTarget] = useState<{ filename: string; position: "before" | "after" } | null>(null);
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [modalFileName, setModalFileName] = useState("");
  const [modalTab, setModalTab] = useState<"create" | "upload">("create");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // Add state for project upload modal
  const [showProjectUploadModal, setShowProjectUploadModal] = useState(false);
  const [uploadedProject, setUploadedProject] = useState<FileList | null>(null);
  const [addFileError, setAddFileError] = useState<string>("");
  const [renameError, setRenameError] = useState<string>("");
  const [showDeleteFolderDialog, setShowDeleteFolderDialog] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);

  const [isDeletingFolder, setIsDeletingFolder] = useState(false);

  const getAgentIdFromPath = (): string | null => {
    try {
      const pathParts = window.location.pathname.split("/");
      return pathParts[2] || null; // /agents/{agentId}/function
    } catch {
      return null;
    }
  };

  const replaceAllFiles = async (newFiles: FilesState) => {
    // Ensure handler file exists
    const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
    if (!newFiles[handlerFileName]) {
      newFiles[handlerFileName] = {
        content: language === "python" ? PYTHON_FUNCTION_TEMPLATE : JAVASCRIPT_FUNCTION_TEMPLATE,
        language,
      };
    }

    // Replace all files with the new content
    setFiles(newFiles);
    onChange(newFiles);

    // Reset all UI state
    setExpandedFolders(new Set());
    setSelectedNode(handlerFileName);
    setFocusedNode(handlerFileName);
    setActiveFolder(null);

    // Only call onActiveFileChange if it's different from current active file
    if (activeFile !== handlerFileName) {
      onActiveFileChange?.(handlerFileName);
    }

    // Update Redux store with zipped code
    const zipBlob = await zipFiles(newFiles);
    const reader = new FileReader();

    reader.onload = () => {
      const base64 = reader.result as string;
      const base64Data = base64.split(",")[1];

      const updatedFormData = {
        ...agentFunctionFormData,
        code: base64Data,
        codePath: true,
        isMultifile: true,
        activeFile: handlerFileName,
      };
      dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
    };

    reader.readAsDataURL(zipBlob);
  };

  // Use provided files or initialize from code
  const [files, setFiles] = useState<FilesState>(propFiles || {});

  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize files from propCode if provided and not already initialized
  useEffect(() => {
    if (propCode && propCode !== lastProcessedCodeRef.current && !isInitializedRef.current) {
      lastProcessedCodeRef.current = propCode;

      const doUnzipCode = async () => {
        try {
          const extracted = await unzipCode(propCode);
          if (!extracted) throw new Error("No files extracted");
          const extractedFiles: FilesState = {};
          for (const [filename, content] of Object.entries(extracted)) {
            const fileLanguage = getLanguageFromExtension(filename);
            extractedFiles[filename] = { content, language: fileLanguage };
          }
          // Ensure handler file exists
          const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
          if (!extractedFiles[handlerFileName]) {
            extractedFiles[handlerFileName] = {
              content: language === "python" ? PYTHON_FUNCTION_TEMPLATE : JAVASCRIPT_FUNCTION_TEMPLATE,
              language,
            };
          }
          setFiles(extractedFiles);
          // Always open handler file first
          if (!propActiveFile) {
            onActiveFileChange?.(handlerFileName);
          }
          onChange(extractedFiles);
          isInitializedRef.current = true;
        } catch (error) {
          console.error("Error unzipping code:", error);
          // Fallback to default structure with handler file
          const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
          const fallbackFiles = {
            [handlerFileName]: {
              content: language === "python" ? PYTHON_FUNCTION_TEMPLATE : JAVASCRIPT_FUNCTION_TEMPLATE,
              language,
            },
          };
          setFiles(fallbackFiles);
          if (!propActiveFile) {
            onActiveFileChange?.(handlerFileName);
          }
          onChange(fallbackFiles);
          isInitializedRef.current = true;
        }
      };

      if (propIsZip) {
        doUnzipCode();
      } else {
        const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
        const simpleFiles = { [handlerFileName]: { content: propCode, language } };
        setFiles(simpleFiles);
        onChange(simpleFiles);
        isInitializedRef.current = true;
      }
    }
  }, [propCode, propIsZip, propActiveFile, onActiveFileChange, onChange, language]);

  // Sync files when propFiles changes
  useEffect(() => {
    if (propFiles && Object.keys(propFiles).length > 0) {
      setFiles(propFiles);
    }
  }, [propFiles]);

  // Ensure propActiveFile is always in open tabs
  useEffect(() => {
    if (propActiveFile && files[propActiveFile] && !openTabs.includes(propActiveFile)) {
      setOpenTabs((prev) => [...prev, propActiveFile]);
    }
  }, [propActiveFile, files, openTabs]);

  // Set default focus to first node
  useEffect(() => {
    if (Object.keys(files).length > 0 && !focusedNode) {
      const tree = buildFileTree(sortedFileNames);
      const filteredTree = filterTreeBySearch(tree, searchQuery);
      const visibleNodes = getVisibleNodes(filteredTree);

      if (visibleNodes.length > 0) {
        const firstNode = visibleNodes[0];
        setFocusedNode(firstNode);
        setSelectedNode(firstNode);
      }
    }
  }, [files, focusedNode]);

  // Reset state when functionId changes (indicating function switch)
  useEffect(() => {
    if (functionId && prevFunctionIdRef.current !== functionId) {
      setFiles({});
      setOpenTabs([]);
      setExpandedFolders(new Set());
      setSelectedNode("");
      setFocusedNode("");
      setActiveFolder(null);
      isInitializedRef.current = false;
      lastProcessedCodeRef.current = undefined;
      prevFunctionIdRef.current = functionId;
    }
  }, [functionId]);

  // Initialize function directory when files are loaded
  useEffect(() => {
    const initializeFunctionDirectory = async () => {
      if (functionId && Object.keys(files).length > 0 && !isInitializedRef.current) {
        try {
          // Get the agent ID from the URL path safely
          const agentId = getAgentIdFromPath();

          if (!agentId) return;

          // Create zip from current files
          const zipBlob = await zipFiles(files);
          const reader = new FileReader();
          const zipData = await new Promise<string>((resolve) => {
            reader.onload = () => {
              const base64 = reader.result as string;
              const base64Data = base64.split(",")[1];
              resolve(base64Data);
            };
            reader.readAsDataURL(zipBlob);
          });

          // Initialize function directory
          await makeRequest<{ tempDir: string }>(`/v1/agents/${agentId}/functions/${functionId}/initialize-function`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: {
              zipData,
            },
          });
        } catch (error) {
          console.error("Error initializing function directory:", error);
        }
      }
    };

    initializeFunctionDirectory();
  }, [functionId, files, isInitializedRef.current]);

  // Simple active file logic - use prop or fallback to first open tab
  const sortedFileNames = Object.keys(files).sort();
  const activeFile = propActiveFile || openTabs[0] || "";
  const currentFileData = files[activeFile];

  // Utility functions
  const getLanguageFromExtension = (filename: string): string => {
    const ext = filename.split(".").pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      py: "python",
      js: "javascript",
      ts: "typescript",
      jsx: "javascript",
      tsx: "typescript",
      json: "json",
      yml: "yaml",
      yaml: "yaml",
      html: "html",
      css: "css",
      md: "markdown",
      sql: "sql",
      xml: "xml",
    };
    return languageMap[ext || ""] || "plaintext";
  };

  const handleFileChange = useCallback(
    async (value: string | undefined) => {
      if (!activeFile) return;

      const content = value || "";
      const updatedFiles = {
        ...files,
        [activeFile]: {
          ...files[activeFile],
          content,
        },
      };

      setFiles(updatedFiles);
      onChange(updatedFiles);

      // Update Redux store with zipped code for backend compatibility
      try {
        const zipBlob = await zipFiles(updatedFiles);
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(",")[1];
          const updatedFormData = {
            ...agentFunctionFormData,
            code: base64Data,
            codePath: true,
            isMultifile: true,
            activeFile: propActiveFile || activeFile,
          };
          dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
        };
        reader.readAsDataURL(zipBlob);
      } catch (error) {
        console.error("Error zipping files:", error);
      }
    },
    [files, activeFile, onChange, agentFunctionFormData, dispatch, propActiveFile],
  );

  const getParentFolder = (filePath: string): string | null => {
    const parts = filePath.split("/");
    if (parts.length <= 1) return null; // File is at root level
    return parts.slice(0, -1).join("/");
  };

  // Add function to handle project upload
  const handleProjectUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setUploadedProject(files);
  };

  const uploadProject = async () => {
    if (!uploadedProject || uploadedProject.length === 0) return;

    try {
      const newFiles: FilesState = {};

      // Process each file in the uploaded project
      for (let i = 0; i < uploadedProject.length; i++) {
        const file = uploadedProject[i];

        // Skip directories (they'll be handled by file paths)
        if (file.webkitRelativePath) {
          const filePath = file.webkitRelativePath;

          // Check if file should be ignored
          if (shouldIgnoreFile(filePath)) {
            continue;
          }

          // Strip the root folder name from the path
          // webkitRelativePath format: "folderName/file1.txt" or "folderName/subfolder/file2.txt"
          const pathParts = filePath.split("/");
          if (pathParts.length > 1) {
            // Remove the first part (root folder name) and join the rest
            const relativePath = pathParts.slice(1).join("/");

            // Check if the relative path should also be ignored
            if (shouldIgnoreFile(relativePath)) {
              continue;
            }

            // Read file content
            const content = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target?.result as string);
              reader.readAsText(file);
            });

            const fileLanguage = getLanguageFromExtension(relativePath);
            newFiles[relativePath] = { content, language: fileLanguage };
          }
        }
      }

      // Replace all files with the new project
      await replaceAllFiles(newFiles);

      // Close modal and reset state
      setShowProjectUploadModal(false);
      setUploadedProject(null);
    } catch (error) {
      console.error("Error uploading project:", error);
      alert("Error uploading project. Please try again.");
    }
  };

  // Function to download project as zip
  const downloadProject = async () => {
    try {
      const zipBlob = await zipFiles(files);
      // Create download link
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      // Set the download filename to <function_name>_MM_DD_YYYY.zip
      const today = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const mm = pad(today.getMonth() + 1);
      const dd = pad(today.getDate());
      const yyyy = today.getFullYear();
      const functionName = agentFunctionFormData?.name || "project";
      link.download = `${functionName}_${mm}_${dd}_${yyyy}.zip`;
      // Trigger download
      document.body.appendChild(link);
      link.click();
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading project:", error);
      alert("Error downloading project. Please try again.");
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    const file = files[0];
    setUploadedFile(file);

    // Auto-fill the filename with the uploaded file's name
    const fileName = file.name;
    setModalFileName(fileName);
  };

  const addNewFile = async (filename?: string, fileContent?: string) => {
    const fileNameToUse = filename || modalFileName;
    if (!fileNameToUse) return;

    // Determine the path for the new file using activeFolder
    const targetPath = activeFolder || "";
    const fullPath = targetPath ? `${targetPath}/${fileNameToUse}` : fileNameToUse;

    if (files[fullPath]) {
      setAddFileError("A file with that name already exists in this location.");
      return;
    }
    setAddFileError("");

    const fileLanguage = getLanguageFromExtension(fullPath);
    const updatedFiles = {
      ...files,
      [fullPath]: {
        content: fileContent || "",
        language: fileLanguage,
      },
    };
    setFiles(updatedFiles);
    onChange(updatedFiles);

    // Open new file in a tab
    setOpenTabs((prev) => [...prev, fullPath]);
    onActiveFileChange?.(fullPath);

    // If adding to a folder, ensure it's expanded
    if (targetPath) {
      setExpandedFolders((prev) => {
        const newSet = new Set(prev);
        newSet.add(targetPath);
        return newSet;
      });
    }

    // Update Redux store with zipped code
    try {
      const zipBlob = await zipFiles(updatedFiles);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        const updatedFormData = {
          ...agentFunctionFormData,
          code: base64Data,
          codePath: true,
          isMultifile: true,
          activeFile: fullPath,
        };
        dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
      };
      reader.readAsDataURL(zipBlob);
    } catch (error) {
      console.error("Error zipping files after add:", error);
    }

    setModalFileName("");
    setUploadedFile(null);
    setShowAddFileModal(false);
    setAddFileError("");
  };

  const removeFile = async (filename: string) => {
    if (Object.keys(files).length > 1) {
      const newFiles = { ...files };
      delete newFiles[filename];
      setFiles(newFiles);
      onChange(newFiles);

      // Also remove from open tabs
      setOpenTabs((prev) => prev.filter((tab) => tab !== filename));

      let newActiveFile = activeFile;
      if (activeFile === filename) {
        const sortedNewFiles = Object.keys(newFiles).sort();
        newActiveFile = sortedNewFiles[0];
        onActiveFileChange?.(newActiveFile);
      }

      // Update Redux store with zipped code for backend compatibility
      try {
        const zipBlob = await zipFiles(newFiles);
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(",")[1];
          const updatedFormData = {
            ...agentFunctionFormData,
            code: base64Data,
            codePath: true,
            isMultifile: true,
            activeFile: newActiveFile,
          };
          dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
        };
        reader.readAsDataURL(zipBlob);
      } catch (error) {
        console.error("Error zipping files after delete:", error);
      }
    }
  };

  const closeTab = (filename: string) => {
    // Close tab without deleting file
    const tabIndex = openTabs.indexOf(filename);
    const newOpenTabs = openTabs.filter((tab) => tab !== filename);
    setOpenTabs(newOpenTabs);

    // If closing the active tab, switch to a tab immediately next to it (like VSCode)
    if (activeFile === filename && newOpenTabs.length > 0) {
      // Prefer the tab to the right, otherwise the one to the left
      let newActiveFile: string | undefined;
      if (tabIndex < newOpenTabs.length) {
        newActiveFile = newOpenTabs[tabIndex]; // right neighbor
      } else if (tabIndex - 1 >= 0) {
        newActiveFile = newOpenTabs[tabIndex - 1]; // left neighbor
      } else {
        newActiveFile = newOpenTabs[0]; // fallback
      }
      onActiveFileChange?.(newActiveFile);
    } else if (newOpenTabs.length === 0) {
      // No tabs left, clear active file
      onActiveFileChange?.("");
    }
  };

  const handleActiveFileChange = (filename: string, fromTree: boolean = false) => {
    let updatedOpenTabs = [...openTabs];
    const currentActiveIndex = updatedOpenTabs.indexOf(activeFile);
    const isAlreadyOpen = updatedOpenTabs.includes(filename);

    if (fromTree && filename !== activeFile) {
      let insertIndex = currentActiveIndex >= 0 ? currentActiveIndex + 1 : updatedOpenTabs.length;

      if (isAlreadyOpen) {
        // Remove from current position
        updatedOpenTabs = updatedOpenTabs.filter((tab) => tab !== filename);
      }

      // Insert at the calculated position
      updatedOpenTabs.splice(insertIndex, 0, filename);
      setOpenTabs(updatedOpenTabs);
    } else if (!isAlreadyOpen) {
      // For non-tree selections or new files, just add to end if needed
      updatedOpenTabs.push(filename);
      setOpenTabs(updatedOpenTabs);
    }

    // Always update active file
    onActiveFileChange?.(filename);
  };

  const showContextMenu = (e: React.MouseEvent, filename: string) => {
    e.preventDefault();
    if (readOnly) return;
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      targetFile: filename,
    });
  };

  // Helper to check if a path is a folder (has children)
  const isFolderPath = (path: string) => Object.keys(files).some((f) => f.startsWith(`${path}/`));

  // Remove a folder and all its contents
  const removeFolder = async (folderPath: string) => {
    // Remove all files under the folder
    const newFiles = { ...files };
    const toDelete = Object.keys(newFiles).filter((f) => f === folderPath || f.startsWith(`${folderPath}/`));
    toDelete.forEach((f) => delete newFiles[f]);
    setFiles(newFiles);
    onChange(newFiles);

    // Remove from open tabs
    setOpenTabs((prev) => prev.filter((tab) => !toDelete.includes(tab)));

    // Update active file if needed
    let newActiveFile = activeFile;
    if (activeFile && toDelete.includes(activeFile)) {
      const sortedNewFiles = Object.keys(newFiles).sort();
      newActiveFile = sortedNewFiles[0] || "";
      onActiveFileChange?.(newActiveFile);
    }

    // Update Redux store with zipped code for backend compatibility
    try {
      const zipBlob = await zipFiles(newFiles);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        const updatedFormData = {
          ...agentFunctionFormData,
          code: base64Data,
          codePath: true,
          isMultifile: true,
          activeFile: newActiveFile,
        };
        dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
      };
      reader.readAsDataURL(zipBlob);
    } catch (error) {
      console.error("Error zipping files after folder delete:", error);
    }
  };

  const hideContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, targetFile: "" });
  };

  const handleDeleteFile = () => {
    const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
    if (contextMenu.targetFile === handlerFileName) {
      // Don't allow deletion of handler file
      return;
    }
    if (contextMenu.targetFile) {
      if (isFolderPath(contextMenu.targetFile)) {
        setFolderToDelete(contextMenu.targetFile);
        setShowDeleteFolderDialog(true);
      } else {
        removeFile(contextMenu.targetFile);
      }
    }
    hideContextMenu();
  };

  const handleRenameFile = () => {
    if (contextMenu.targetFile) {
      setRenamingFile(contextMenu.targetFile);
      setRenameValue(contextMenu.targetFile.split("/").pop() || contextMenu.targetFile);
      setRenameError("");
    }
    hideContextMenu();
  };

  const handleRenameConfirm = async () => {
    if (renamingFile && renameValue && renameValue !== renamingFile) {
      // Only allow renaming for files
      if (Object.keys(files).some((f) => f.startsWith(`${renamingFile}/`))) {
        setRenamingFile("");
        setRenameValue("");
        setRenameError("");
        return;
      }
      // Prevent renaming to an existing file at the same level
      const parentPath = renamingFile.includes("/") ? renamingFile.slice(0, renamingFile.lastIndexOf("/")) : "";
      const newPath = parentPath ? `${parentPath}/${renameValue}` : renameValue;
      const siblingPaths = Object.keys(files).filter((f) => {
        const siblingParent = f.includes("/") ? f.slice(0, f.lastIndexOf("/")) : "";
        const siblingName = f.includes("/") ? f.slice(f.lastIndexOf("/") + 1) : f;
        return siblingParent === parentPath && siblingName === renameValue;
      });
      if ((files[newPath] && newPath !== renamingFile) || siblingPaths.length > 0) {
        setRenameError("A file with that name already exists.");
        return;
      }
      setRenameError("");

      const updatedFiles: FilesState = { ...files };
      updatedFiles[newPath] = updatedFiles[renamingFile];
      delete updatedFiles[renamingFile];
      setFiles(updatedFiles);
      onChange(updatedFiles);

      // Update open tabs
      setOpenTabs((prev) => prev.map((tab) => (tab === renamingFile ? newPath : tab)));

      // Update active file if it was the renamed file
      if (activeFile === renamingFile) {
        onActiveFileChange?.(newPath);
      } else if (!activeFile || !updatedFiles[activeFile]) {
        onActiveFileChange?.(newPath);
      }

      // Update Redux store with zipped code for backend compatibility
      try {
        const zipBlob = await zipFiles(updatedFiles);
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(",")[1];
          const updatedFormData = {
            ...agentFunctionFormData,
            code: base64Data,
            codePath: true,
            isMultifile: true,
            activeFile: activeFile === renamingFile ? newPath : activeFile,
          };
          dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
        };
        reader.readAsDataURL(zipBlob);
      } catch (error) {
        console.error("Error zipping files after rename:", error);
      }
      setRenamingFile("");
      setRenameValue("");
      setRenameError("");
    }
    setRenamingFile("");
    setRenameValue("");
    setRenameError("");
  };

  const handleRenameCancel = () => {
    setRenamingFile("");
    setRenameValue("");
    setRenameError("");
  };

  const moveNode = async (sourcePath: string, targetPath: string) => {
    if (readOnly) return;
    if (sourcePath === targetPath) return;
    if (targetPath.startsWith(`${sourcePath}/`)) {
      return;
    }

    const updatedFiles = { ...files };
    const affectedPaths = Object.keys(updatedFiles).filter((p) => p.startsWith(`${sourcePath}/`) || p === sourcePath);

    const sourceName = sourcePath.split("/").pop() || sourcePath;
    const pathMap = new Map<string, string>();
    affectedPaths.forEach((oldPath) => {
      const relative = oldPath.substring(sourcePath.length);
      const newPath = targetPath ? `${targetPath}/${sourceName}${relative}` : `${sourceName}${relative}`;
      pathMap.set(oldPath, newPath);
    });

    pathMap.forEach((newP, oldP) => {
      updatedFiles[newP] = updatedFiles[oldP];
      delete updatedFiles[oldP];
    });

    setFiles(updatedFiles);
    onChange(updatedFiles);

    // Update open tabs
    setOpenTabs((prev) => prev.map((tab) => pathMap.get(tab) || tab));

    // Update active file
    let newActiveFile = activeFile;
    if (activeFile && pathMap.has(activeFile)) {
      newActiveFile = pathMap.get(activeFile)!;
      onActiveFileChange?.(newActiveFile);
    }

    // Update expanded folders
    setExpandedFolders((prev) => {
      const newSet = new Set<string>();
      prev.forEach((oldExp) => {
        if (pathMap.has(oldExp)) {
          newSet.add(pathMap.get(oldExp)!);
        } else if (!affectedPaths.includes(oldExp)) {
          newSet.add(oldExp);
        }
      });
      return newSet;
    });

    // Update selected, focused, activeFolder
    const updateStatePath = (state: string) => pathMap.get(state) || state;
    setSelectedNode(updateStatePath(selectedNode));
    setFocusedNode(updateStatePath(focusedNode));
    if (activeFolder !== null) {
      setActiveFolder(pathMap.get(activeFolder) ?? activeFolder);
    }

    // Zip and update Redux
    try {
      const zipBlob = await zipFiles(updatedFiles);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        const updatedFormData = {
          ...agentFunctionFormData,
          code: base64Data,
          codePath: true,
          isMultifile: true,
          activeFile: newActiveFile,
        };
        dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
      };
      reader.readAsDataURL(zipBlob);
    } catch (error) {
      console.error("Error zipping after move:", error);
    }
  };

  // Monaco editor setup with advanced features
  const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor, monacoInstance: typeof monaco) => {
    editorRef.current = editor;

    // Set up Python language support
    monacoInstance.languages.setMonarchTokensProvider("python", python);

    // Custom autocomplete for Python
    monacoInstance.languages.registerCompletionItemProvider("python", {
      provideCompletionItems: (model: monaco.editor.ITextModel, position: monaco.Position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions = [
          {
            label: "print",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "print(${1:value})",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Print values to the console",
          },
          {
            label: "def",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "def ${1:function_name}(${2:parameters}):\n\t${3:pass}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define a function",
          },
          {
            label: "class",
            kind: monacoInstance.languages.CompletionItemKind.Class,
            insertText: "class ${1:ClassName}:\n\t${2:pass}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define a class",
          },
          {
            label: "async def",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "async def ${1:function_name}(${2:parameters}):\n\t${3:pass}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define an async function",
          },
        ];
        return { suggestions };
      },
    });

    // Custom autocomplete for JavaScript
    monacoInstance.languages.registerCompletionItemProvider("javascript", {
      provideCompletionItems: (model: monaco.editor.ITextModel, position: monaco.Position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions = [
          {
            label: "console.log",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "console.log(${1:value});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Log values to the console",
          },
          {
            label: "function",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "function ${1:functionName}(${2:parameters}) {\n\t${3:// code}\n}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define a function",
          },
          {
            label: "async function",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "async function ${1:functionName}(${2:parameters}) {\n\t${3:// code}\n}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define an async function",
          },
        ];
        return { suggestions };
      },
    });

    // Add custom keybindings (excluding Ctrl+S which is handled by the layout)
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyF, () => {
      const findAction = editor.getAction("actions.find");
      if (findAction) {
        findAction.run();
      }
    });

    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyF, () => {
      const findReplaceAction = editor.getAction("editor.action.startFindReplaceAction");
      if (findReplaceAction) {
        findReplaceAction.run();
      }
    });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        hideContextMenu();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [contextMenu.visible]);

  // Tree structure for files and folders
  interface TreeNode {
    name: string;
    type: "file" | "folder";
    path: string;
    children?: TreeNode[];
    isExpanded?: boolean;
  }

  // Search result structure
  interface SearchResult {
    filePath: string;
    fileName: string;
    lineNumber: number;
    lineContent: string;
    matchStart: number;
    matchEnd: number;
    contextBefore?: string;
    contextAfter?: string;
  }

  // Alphabetical sorting: hidden files/dirs first, then folders before files, then alphabetically
  const sortTreeNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes.sort((a, b) => {
      const aHidden = a.name.startsWith(".");
      const bHidden = b.name.startsWith(".");

      // Hidden items come first
      if (aHidden !== bHidden) return aHidden ? -1 : 1;

      // Then sort by type (folders before files)
      if (a.type !== b.type) {
        if (a.type === "folder" && b.type === "file") return -1;
        if (a.type === "file" && b.type === "folder") return 1;
      }

      // Then sort alphabetically by name (case-insensitive)
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  };

  const buildFileTree = (fileNames: string[]): TreeNode[] => {
    const tree: TreeNode[] = [];
    const nodeMap = new Map<string, TreeNode>();

    // Create root node
    const root: TreeNode = { name: "", type: "folder", path: "", children: [] };
    nodeMap.set("", root);

    // Process each file path
    fileNames.forEach((filePath) => {
      const parts = filePath.split("/");
      let currentPath = "";
      let currentNode = root;

      // Process each part of the path
      parts.forEach((part, index) => {
        const isLastPart = index === parts.length - 1;
        const newPath = currentPath ? `${currentPath}/${part}` : part;

        if (!nodeMap.has(newPath)) {
          const node: TreeNode = {
            name: part,
            type: isLastPart ? "file" : "folder",
            path: newPath,
            children: isLastPart ? undefined : [],
          };
          nodeMap.set(newPath, node);
          currentNode.children?.push(node);
        }

        currentNode = nodeMap.get(newPath)!;
        currentPath = newPath;
      });
    });

    // Sort all children at every level using VSCode-style sorting
    const sortAllChildren = (node: TreeNode): void => {
      if (node.children) {
        node.children = sortTreeNodes(node.children);
        node.children.forEach(sortAllChildren);
      }
    };

    sortAllChildren(root);

    return root.children || [];
  };

  const toggleFolder = (folderPath: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  // Get flattened list of visible nodes for keyboard navigation
  const getVisibleNodes = (nodes: TreeNode[]): string[] => {
    const visible: string[] = [];

    const traverse = (nodeList: TreeNode[]) => {
      for (const node of nodeList) {
        visible.push(node.path);
        if (node.type === "folder" && expandedFolders.has(node.path) && node.children) {
          traverse(node.children);
        }
      }
    };

    traverse(nodes);
    return visible;
  };

  // Navigate tree with arrow keys
  const navigateTree = (direction: "up" | "down") => {
    const tree = buildFileTree(sortedFileNames);
    const filteredTree = filterTreeBySearch(tree, searchQuery);
    const visibleNodes = getVisibleNodes(filteredTree);

    const currentIndex = visibleNodes.indexOf(focusedNode);
    let newIndex = currentIndex;

    if (direction === "up" && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === "down" && currentIndex < visibleNodes.length - 1) {
      newIndex = currentIndex + 1;
    }

    if (newIndex !== currentIndex) {
      const newFocusedNode = visibleNodes[newIndex];
      setFocusedNode(newFocusedNode);
      setSelectedNode(newFocusedNode);

      // Focus the DOM element
      const element = document.querySelector(`[data-tree-node="${newFocusedNode}"]`) as HTMLElement;
      if (element) {
        element.focus();
      }
    }
  };

  const filterTreeBySearch = (nodes: TreeNode[], query: string): TreeNode[] => {
    if (!query.trim()) return nodes;

    const filtered: TreeNode[] = [];

    nodes.forEach((node) => {
      if (node.type === "file") {
        // Include files that match the search query
        if (node.name.toLowerCase().includes(query.toLowerCase())) {
          filtered.push(node);
        }
      } else {
        // For folders, check if any children match
        const filteredChildren = filterTreeBySearch(node.children || [], query);
        if (filteredChildren.length > 0) {
          filtered.push({
            ...node,
            children: filteredChildren,
          });
        }
      }
    });

    // Apply VSCode-style sorting to filtered results
    return sortTreeNodes(filtered);
  };

  // Global content search function with performance optimizations
  const searchFileContents = useCallback(
    (query: string): SearchResult[] => {
      if (!query.trim() || query.length < 2) return []; // Don't search single characters

      const results: SearchResult[] = [];
      const searchRegex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");

      Object.entries(files).forEach(([filePath, fileData]) => {
        const lines = fileData.content.split("\n");

        lines.forEach((line, index) => {
          let match;
          const lineContent = line;

          // Reset regex for multiple matches in same line
          searchRegex.lastIndex = 0;

          while ((match = searchRegex.exec(lineContent)) !== null) {
            const trimmedLine = lineContent.trim();
            const trimOffset = lineContent.indexOf(trimmedLine);
            const adjustedMatchStart = Math.max(0, match.index - trimOffset);
            const adjustedMatchEnd = Math.max(0, match.index + match[0].length - trimOffset);

            results.push({
              filePath,
              fileName: filePath.split("/").pop() || filePath,
              lineNumber: index + 1,
              lineContent: trimmedLine,
              matchStart: adjustedMatchStart,
              matchEnd: adjustedMatchEnd,
              contextBefore: index > 0 ? lines[index - 1]?.trim() : undefined,
              contextAfter: index < lines.length - 1 ? lines[index + 1]?.trim() : undefined,
            });

            // Prevent infinite loops with zero-width matches
            if (match.index === searchRegex.lastIndex) {
              searchRegex.lastIndex++;
            }
          }
        });
      });

      return results;
    },
    [files],
  );

  // Debounced search query for better performance
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    if (searchMode === "content" && searchQuery.trim().length > 0) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setIsSearching(false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery, searchMode]);

  // Handle search mode and results with memoization
  const searchResults = useMemo(() => {
    return searchMode === "content" ? searchFileContents(debouncedSearchQuery) : [];
  }, [searchMode, debouncedSearchQuery, searchFileContents]);

  // Track the last search query to prevent duplicate expansions
  const lastExpandedQuery = useRef<string>("");

  // Update search results visibility (use debounced query for content search)
  React.useEffect(() => {
    if (searchMode === "content") {
      setShowSearchResults(debouncedSearchQuery.trim().length > 0);

      // Auto-expand files when starting a new search (only if query changed)
      if (debouncedSearchQuery.trim().length > 0 && searchResults.length > 0 && lastExpandedQuery.current !== debouncedSearchQuery) {
        const fileKeys = new Set(searchResults.map((result: SearchResult) => result.filePath));
        setExpandedSearchFiles(fileKeys);
        lastExpandedQuery.current = debouncedSearchQuery;
      }
    } else {
      setShowSearchResults(false);
      setExpandedSearchFiles(new Set());
      lastExpandedQuery.current = "";
    }
  }, [searchMode, debouncedSearchQuery, searchResults]);

  // Toggle file expansion in search results
  const toggleSearchFileExpansion = (filePath: string) => {
    setExpandedSearchFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filePath)) {
        newSet.delete(filePath);
      } else {
        newSet.add(filePath);
      }
      return newSet;
    });
  };

  // Navigate to search result
  const navigateToSearchResult = (result: SearchResult) => {
    // Open the file
    handleActiveFileChange(result.filePath, true);

    // Focus the editor and navigate to line (with a small delay to ensure editor is mounted)
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.revealLineInCenter(result.lineNumber);
        editorRef.current.setPosition({ lineNumber: result.lineNumber, column: result.matchStart + 1 });
        editorRef.current.focus();
      }
    }, 100);
  };

  // Handle sidebar resizing
  const startResize = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true);
      e.preventDefault();

      const startX = e.clientX;
      const startWidth = sidebarWidth;

      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(150, Math.min(600, startWidth + diff)); // Min 150px, Max 600px
        setSidebarWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [sidebarWidth],
  );

  // Enhanced tree node rendering with keyboard navigation
  const renderTreeNode = (node: TreeNode, depth: number = 0): JSX.Element => {
    const isExpanded = expandedFolders.has(node.path);
    const isSelected = selectedNode === node.path;
    const isFocused = focusedNode === node.path;
    const isActive = activeFile === node.path;
    const isActiveFolder = activeFolder === node.path;
    const isInActiveFolder = activeFolder && node.path.startsWith(activeFolder + "/");
    const isActiveFolderNull = activeFolder === null;
    const shouldShowActiveBorder = isActiveFolder || isInActiveFolder || isActiveFolderNull;
    const paddingLeft = depth * 16; // 16px per level

    const handleKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          if (node.type === "folder") {
            toggleFolder(node.path);
            setActiveFolder(node.path);
          } else {
            handleActiveFileChange(node.path, true);
            setActiveFolder(getParentFolder(node.path));
          }
          break;
        case "ArrowRight":
          e.preventDefault();
          if (node.type === "folder" && !isExpanded) {
            toggleFolder(node.path);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (node.type === "folder" && isExpanded) {
            toggleFolder(node.path);
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          navigateTree("up");
          break;
        case "ArrowDown":
          e.preventDefault();
          navigateTree("down");
          break;
      }
    };

    const handleClick = () => {
      setSelectedNode(node.path);
      setFocusedNode(node.path);
      if (node.type === "folder") {
        toggleFolder(node.path);
        setActiveFolder(node.path); // Set active folder when folder is clicked
      } else {
        handleActiveFileChange(node.path, true);
        setActiveFolder(getParentFolder(node.path)); // Set active folder to parent of file
      }
    };

    const handleFocus = () => {
      setFocusedNode(node.path);
    };

    if (node.type === "folder") {
      const effectiveExpanded = searchQuery && (node.children?.length ?? 0) > 0 ? true : expandedFolders.has(node.path);

      return (
        <div key={node.path}>
          <div
            draggable={!readOnly}
            onDragStart={(e) => {
              e.dataTransfer.setData("text/plain", node.path);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => {
              e.preventDefault();
              setDropTarget(node.path);
            }}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(e) => {
              e.preventDefault();
              const sourcePath = e.dataTransfer.getData("text/plain");
              moveNode(sourcePath, node.path);
              setDropTarget(null);
            }}
            className={clsx(
              "flex items-center px-3 py-2 cursor-pointer text-sm border-b border-gray-200 dark:border-gray-600 outline-none",
              "hover:bg-gray-100 dark:hover:bg-gray-700",
              isSelected && "bg-brand/10 dark:bg-brand/20",
              dropTarget === node.path && "bg-brand/20 dark:bg-brand/30",
              shouldShowActiveBorder && "border-l-2 !border-l-brand",
              isFocused && "ring-2 ring-brand ring-inset",
            )}
            style={{ paddingLeft: paddingLeft + 12 }}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onContextMenu={(e) => showContextMenu(e, node.path)}
            tabIndex={0}
            role="treeitem"
            aria-expanded={effectiveExpanded}
            aria-label={`${node.name} folder`}
            data-tree-node={node.path}
          >
            {effectiveExpanded ? (
              <Icon
                name="chevron-down"
                size={16}
                className="mr-1 text-gray-500 dark:text-gray-400"
              />
            ) : (
              <Icon
                name="chevron-right"
                size={16}
                className="mr-1 text-gray-500 dark:text-gray-400"
              />
            )}
            {effectiveExpanded ? (
              <Icon
                name="folder_open"
                size={16}
                className="mr-2 text-gray-500 dark:text-gray-400"
              />
            ) : (
              <Icon
                name="folder"
                size={16}
                className="mr-2 text-gray-500 dark:text-gray-400"
              />
            )}
            <span className="flex-1 text-gray-900 dark:text-white font-medium truncate min-w-0">{node.name}</span>
          </div>
          {effectiveExpanded && node.children && (
            <div
              role="group"
              aria-labelledby={`folder-${node.path}`}
            >
              {node.children.map((child) => renderTreeNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          key={node.path}
          draggable={!readOnly}
          onDragStart={(e) => {
            e.dataTransfer.setData("text/plain", node.path);
          }}
          className={clsx(
            "flex items-center px-3 py-2 cursor-pointer text-sm border-b border-gray-200 dark:border-gray-600 outline-none",
            "hover:bg-gray-100 dark:hover:bg-gray-700",
            isActive && "bg-brand/10 dark:bg-brand/20 border-brand/20 dark:border-brand/30",
            isSelected && !isActive && "bg-gray-50 dark:bg-gray-800",
            shouldShowActiveBorder && "border-l-2 !border-l-brand",
            isFocused && "ring-2 ring-brand ring-inset",
          )}
          style={{ paddingLeft: paddingLeft + 12 }}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onContextMenu={(e) => showContextMenu(e, node.path)}
          tabIndex={0}
          role="treeitem"
          aria-label={`${node.name} file`}
          data-tree-node={node.path}
        >
          <div className="flex-shrink-0 mr-2">
            <Icon
              name="file"
              size={16}
              className="text-gray-500 dark:text-gray-400"
            />
          </div>
          {renamingFile === node.path && node.type === "file" ? (
            <div className="flex-1 flex flex-col">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => {
                  setRenameValue(e.target.value);
                  setRenameError("");
                }}
                onKeyDown={(e) => {
                  e.stopPropagation(); // Prevent tree navigation
                  if (e.key === "Enter") {
                    handleRenameConfirm();
                  } else if (e.key === "Escape") {
                    handleRenameCancel();
                  }
                  // Allow arrow keys for cursor movement
                }}
                onBlur={handleRenameCancel}
                onClick={(e) => e.stopPropagation()} // Prevent tree selection
                className="bg-white dark:bg-gray-700 border border-blue-500 rounded px-1 text-gray-900 dark:text-white text-sm"
                style={{
                  width: `calc(175px - 20px - ${depth * 16}px - 16px)` as any,
                  marginLeft: 0,
                  display: "block",
                }}
                autoFocus
              />
              {renameError && <div className="text-xs text-red-600 mt-1">{renameError}</div>}
            </div>
          ) : (
            <span className="flex-1 text-gray-900 dark:text-white truncate min-w-0">{node.name}</span>
          )}
        </div>
      );
    }
  };

  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
          <Icon
            name="search"
            size={24}
            className="mb-2 animate-pulse"
          />
          <span className="text-sm">Searching...</span>
        </div>
      );
    }

    if (searchResults.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
          <Icon
            name="search"
            size={24}
            className="mb-2"
          />
          <span className="text-sm">{debouncedSearchQuery.trim() ? "No matches found" : "Start typing to search"}</span>
        </div>
      );
    }

    // Group results by file
    const resultsByFile = searchResults.reduce(
      (acc: Record<string, SearchResult[]>, result: SearchResult) => {
        if (!acc[result.filePath]) {
          acc[result.filePath] = [];
        }
        acc[result.filePath].push(result);
        return acc;
      },
      {} as Record<string, SearchResult[]>,
    );

    return (
      <div className="flex-1 flex flex-col">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-400 flex-shrink-0">
          {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} in {Object.keys(resultsByFile).length} file
          {Object.keys(resultsByFile).length !== 1 ? "s" : ""}
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-1 p-1">
            {Object.entries(resultsByFile).map(([filePath, results]: [string, SearchResult[]]) => (
              <div
                key={filePath}
                className="border-b border-gray-100 dark:border-gray-700 last:border-b-0"
              >
                {/* File header */}
                <div
                  className="sticky top-0 z-10 px-3 py-2 bg-gray-50 dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-600"
                  onClick={() => toggleSearchFileExpansion(filePath)}
                >
                  <Icon
                    name={expandedSearchFiles.has(filePath) ? "chevron-down" : "chevron-right"}
                    size={14}
                    className="mr-2 text-gray-500 dark:text-gray-400"
                  />
                  <Icon
                    name="file"
                    size={14}
                    className="mr-2 text-gray-500 dark:text-gray-400"
                  />
                  <span className="truncate">{results[0].fileName}</span>
                  <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                    {results.length} match{results.length !== 1 ? "es" : ""}
                  </span>
                </div>
                {/* Search results for this file */}
                {expandedSearchFiles.has(filePath) && (
                  <div className="space-y-1">
                    {results.map((result: SearchResult, index: number) => (
                      <div
                        key={`${result.filePath}-${result.lineNumber}-${index}`}
                        className="pl-8 pr-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-l-2 border-transparent hover:border-brand transition-colors"
                        onClick={() => navigateToSearchResult(result)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Line {result.lineNumber}</span>
                        </div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          <span className="text-gray-600 dark:text-gray-400">{result.lineContent.substring(0, result.matchStart)}</span>
                          <span className="bg-yellow-200 dark:bg-yellow-600 text-gray-900 dark:text-white px-1 rounded">
                            {result.lineContent.substring(result.matchStart, result.matchEnd)}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">{result.lineContent.substring(result.matchEnd)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFileTree = () => {
    const tree = buildFileTree(sortedFileNames);
    const filteredTree = filterTreeBySearch(tree, searchQuery);

    return (
      <div
        role="tree"
        aria-label="File explorer"
        className="focus:outline-none flex-1 overflow-y-auto"
        tabIndex={-1}
      >
        {filteredTree.map((node) => renderTreeNode(node))}
        {/* Clickable area below files to reset active folder to root */}
        <div
          className={clsx("grow min-h-[40px] cursor-pointer", dropTarget === "" && "bg-brand/20 dark:bg-brand/30")}
          style={{ height: "100%" }}
          onClick={() => setActiveFolder(null)}
          title="Click to set root directory as active folder"
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => {
            e.preventDefault();
            setDropTarget("");
          }}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(e) => {
            e.preventDefault();
            const sourcePath = e.dataTransfer.getData("text/plain");
            moveNode(sourcePath, "");
            setDropTarget(null);
          }}
        />
      </div>
    );
  };

  return (
    <View
      className="relative flex flex-col flex-grow h-full overflow-hidden"
      ref={containerRef}
    >
      <View className="flex flex-col flex-grow z-0">
        <div className="flex flex-1 overflow-hidden">
          {/* File Explorer Sidebar */}
          {showFileExplorer && (
            <div
              className="flex-shrink-0 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col relative"
              style={{ width: `${sidebarWidth}px` }}
            >
              <div className="p-2 pb-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Files</h3>
                  <div className="flex items-center gap-1">
                    {!readOnly && (
                      <Button
                        onClick={() => {
                          setModalFileName("");
                          setShowAddFileModal(true);
                        }}
                        className="p-1"
                        type="button"
                        variant="ghost"
                        title="Add new file"
                      >
                        <Icon
                          name="add"
                          size={16}
                        />
                      </Button>
                    )}
                    <Button
                      onClick={downloadProject}
                      className="p-1"
                      type="button"
                      variant="ghost"
                      title="Download project as ZIP"
                    >
                      <Icon
                        name="download"
                        size={16}
                      />
                    </Button>
                    <Button
                      onClick={() => setShowFileExplorer(false)}
                      className="p-1"
                      title="Hide sidebar"
                      type="button"
                      variant="ghost"
                    >
                      <Icon
                        name="chevron-left"
                        size={16}
                      />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <select
                      value={searchMode}
                      onChange={(e) => {
                        setSearchMode(e.target.value as "files" | "content");
                        setSearchQuery(""); // Clear search when switching modes
                      }}
                      className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1 text-gray-900 dark:text-white"
                    >
                      <option value="content">Find in Files</option>
                      <option value="files">File Names</option>
                    </select>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={searchMode === "files" ? "Search file names..." : "Find in files..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-7 pr-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs text-gray-900 dark:text-white"
                    />
                    <Icon
                      name="search"
                      size={16}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-500 dark:text-gray-400"
                    />
                  </div>
                </div>
              </div>
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">{showSearchResults ? renderSearchResults() : renderFileTree()}</div>
              <div className="p-1.5 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Icon
                    name="settings"
                    size={16}
                  />
                  <select
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                    className="h-5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1 text-gray-900 dark:text-white"
                  >
                    <option value={12}>12px</option>
                    <option value={14}>14px</option>
                    <option value={16}>16px</option>
                    <option value={18}>18px</option>
                  </select>
                </div>
              </div>

              {/* Resize Handle */}
              <div
                className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-brand/50 transition-colors ${
                  isResizing ? "bg-brand" : "bg-transparent"
                }`}
                onMouseDown={startResize}
                title="Drag to resize sidebar"
              />
            </div>
          )}

          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* File Tabs */}
            <View className="bg-gray-50 dark:bg-gray-800 flex items-center overflow-x-auto no-scrollbar border-b border-gray-200 dark:border-gray-700">
              {/* Show expand button in tab bar when sidebar is hidden */}
              {!showFileExplorer && (
                <Button
                  onClick={() => setShowFileExplorer(true)}
                  className="p-1 mx-2"
                  title="Show sidebar"
                  type="button"
                  variant="ghost"
                >
                  <Icon
                    name="chevron-right"
                    size={16}
                  />
                </Button>
              )}
              {openTabs.map((filename) => (
                <div
                  key={filename}
                  draggable={true}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", filename);
                    setTabDropTarget(null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const offsetX = e.clientX - rect.left;
                    const halfWidth = rect.width / 2;
                    const position = offsetX < halfWidth ? "before" : "after";
                    setTabDropTarget({ filename, position });
                  }}
                  onDragEnter={(e) => e.preventDefault()}
                  onDragLeave={() => setTabDropTarget(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!tabDropTarget) return;
                    const draggedFilename = e.dataTransfer.getData("text/plain");
                    if (draggedFilename === tabDropTarget.filename) return;

                    setOpenTabs((prev) => {
                      const newTabs = [...prev];
                      const draggedIndex = newTabs.indexOf(draggedFilename);
                      let targetIndex = newTabs.indexOf(tabDropTarget.filename);

                      if (draggedIndex !== -1 && targetIndex !== -1) {
                        // Remove dragged tab
                        newTabs.splice(draggedIndex, 1);
                        // Adjust index for after position
                        if (tabDropTarget.position === "after") {
                          targetIndex += 1;
                        }
                        // Insert at adjusted position
                        newTabs.splice(targetIndex, 0, draggedFilename);
                      }

                      return newTabs;
                    });

                    setTabDropTarget(null);
                  }}
                  className={clsx(
                    "flex items-center px-4 py-1 border-r border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700",
                    activeFile === filename ? "bg-white dark:bg-gray-700 border-b-2 border-blue-500" : "",
                    tabDropTarget?.filename === filename && tabDropTarget.position === "before" && "!border-l-2 !border-l-brand",
                    tabDropTarget?.filename === filename && tabDropTarget.position === "after" && "!border-r-2 !border-r-blue-500",
                  )}
                  onClick={() => handleActiveFileChange(filename, false)}
                >
                  <Icon
                    name="file"
                    size={14}
                    className="mr-2 text-gray-600 dark:text-gray-400"
                  />
                  <span className="mr-1 text-sm text-gray-900 dark:text-white">{filename.split("/").pop()}</span>
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      closeTab(filename);
                    }}
                    size="icon"
                    className="!h-6 !w-6 !p-0 !m-0"
                    title="Close tab"
                    type="button"
                    variant="ghost"
                  >
                    <Icon
                      name="close"
                      size={16}
                      className="text-gray-600 dark:text-gray-400"
                    />
                  </Button>
                </div>
              ))}
            </View>

            {/* Editor */}
            <View className="flex-1">
              {openTabs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
                  <div className="text-center max-w-md mx-auto p-6">
                    <div className="mb-4">
                      <Icon
                        name="folder"
                        size={64}
                        className="text-gray-400 dark:text-gray-500 mx-auto"
                      />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No files open</h3>
                    {readOnly ? (
                      <p className="text-gray-600 dark:text-gray-400 mb-6">Select a file to the left to open it.</p>
                    ) : (
                      <>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                          Select a file to the left to open, create a new file using the + button in the navigation bar, or upload an entire project folder to
                          get started.
                        </p>
                        <Button
                          variant="brand"
                          onClick={() => setShowProjectUploadModal(true)}
                          className="mb-2 mx-auto"
                        >
                          Upload Project Folder
                        </Button>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mb-8">This will replace all current files with the uploaded project contents.</p>
                      </>
                    )}
                  </div>
                </div>
              ) : showEditor ? (
                <Editor
                  key={activeFile} // Force re-render when active file changes
                  height="100%"
                  language={currentFileData?.language || language}
                  value={currentFileData?.content || ""}
                  onChange={handleFileChange}
                  onMount={handleEditorDidMount}
                  theme={theme === "dark" ? "vs-dark" : "light"}
                  options={{
                    fontSize: fontSize,
                    fontFamily: "JetBrains Mono, Consolas, Monaco, monospace",
                    minimap: { enabled: true },
                    wordWrap: "on",
                    automaticLayout: true,
                    scrollBeyondLastLine: true,
                    tabSize: 4,
                    insertSpaces: true,
                    renderLineHighlight: "all",
                    readOnly,
                    readOnlyMessage: {
                      value:
                        readOnlyMessage ??
                        `Only the latest version can be edited, and deployed versions cannot be edited.${latestNonDeployedVersionName ? ` You can edit "${latestNonDeployedVersionName}" or create a new version.` : " Create a new version to make changes."}`,
                    },
                    bracketPairColorization: { enabled: true },
                    guides: {
                      bracketPairs: true,
                      indentation: true,
                    },
                    suggest: {
                      showKeywords: true,
                      showSnippets: true,
                    },
                    quickSuggestions: {
                      other: true,
                      comments: true,
                      strings: true,
                    },
                    suggestOnTriggerCharacters: true,
                    parameterHints: { enabled: true },
                    snippetSuggestions: "top",
                    formatOnPaste: true,
                    formatOnType: true,
                    scrollbar: {
                      vertical: "visible",
                      horizontal: "visible",
                      useShadows: false,
                      verticalScrollbarSize: 10,
                      horizontalScrollbarSize: 10,
                      verticalSliderSize: 10,
                      horizontalSliderSize: 10,
                      arrowSize: 30,
                    },
                  }}
                />
              ) : (
                <View className="w-full h-full flex-1 bg-black dark:bg-[#1e1e1e] transition-all duration-100 dark:duration-200 opacity-25 dark:opacity-100" />
              )}
            </View>

            {/* Status Bar */}
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 text-xs border-t border-gray-200 dark:border-gray-700 flex justify-between items-center h-12">
              <div className="flex items-center gap-4">
                <span className="text-gray-600 dark:text-gray-400">File: {activeFile}</span>
                <span className="text-gray-600 dark:text-gray-400">Language: {currentFileData?.language || "none"}</span>
                <span className="text-gray-600 dark:text-gray-400">Open Tabs: {openTabs.length}</span>
                <span className="text-gray-600 dark:text-gray-400">Total Files: {Object.keys(files).length}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-gray-600 dark:text-gray-400">Theme: {theme}</span>
                <span className="text-gray-600 dark:text-gray-400">Font: {fontSize}px</span>
              </div>
            </div>
          </div>
        </div>
      </View>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1 min-w-[120px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          {!isFolderPath(contextMenu.targetFile) && (
            <button
              onClick={handleRenameFile}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <span>Rename</span>
            </button>
          )}
          <button
            onClick={handleDeleteFile}
            className={clsx(
              "w-full px-4 py-2 text-left text-sm flex items-center gap-2",
              contextMenu.targetFile === `handler.${language === "python" ? "py" : "js"}`
                ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                : "text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700",
            )}
            disabled={contextMenu.targetFile === `handler.${language === "python" ? "py" : "js"}`}
          >
            <span>Delete</span>
          </button>
        </div>
      )}

      {/* Add File Modal */}
      <Dialog
        isOpen={showAddFileModal}
        onClose={() => {
          setShowAddFileModal(false);
          setModalFileName("");
          setUploadedFile(null);
        }}
        title="Add New File"
        size="md"
        actions={
          <div className="flex gap-2">
            <Button
              variant="neutral"
              onClick={() => {
                setShowAddFileModal(false);
                setModalFileName("");
                setUploadedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={async () => {
                if (modalFileName.trim()) {
                  if (modalTab === "upload" && uploadedFile) {
                    // Read file content and add it
                    const reader = new FileReader();
                    reader.onload = async (e) => {
                      const content = e.target?.result as string;
                      await addNewFile(modalFileName.trim(), content);
                    };
                    reader.readAsText(uploadedFile);
                  } else {
                    // Create empty file
                    await addNewFile(modalFileName.trim());
                  }
                }
              }}
              isDisabled={!modalFileName.trim() || (modalTab === "upload" && !uploadedFile)}
            >
              Add File
            </Button>
          </div>
        }
      >
        <div className="p-4">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            <button
              onClick={() => setModalTab("create")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                modalTab === "create"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Create New
            </button>
            <button
              onClick={() => setModalTab("upload")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                modalTab === "upload"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Upload File
            </button>
          </div>

          {/* Tab Content */}
          {modalTab === "create" ? (
            <div>
              <InputField
                label="File Name"
                placeholder={`filename.${language === "python" ? "py" : "js"}`}
                value={modalFileName}
                onChange={(val) => {
                  setModalFileName(val);
                  setAddFileError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && modalFileName.trim()) {
                    addNewFile(modalFileName.trim());
                  } else if (e.key === "Escape") {
                    setShowAddFileModal(false);
                    setModalFileName("");
                    setAddFileError("");
                  }
                }}
              />
              {addFileError && <div className="text-xs text-red-600 mt-1">{addFileError}</div>}
              {activeFolder && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  File will be created in: <span className="font-medium">{activeFolder}/</span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <InputField
                label="File Name"
                placeholder="Enter filename (optional - will use uploaded file name)"
                value={modalFileName}
                onChange={(val) => {
                  setModalFileName(val);
                  setAddFileError("");
                }}
              />
              {addFileError && <div className="text-xs text-red-600 mt-1">{addFileError}</div>}
              <div className="mt-4">
                <FileField
                  onChange={handleFileUpload}
                  fileTypes={["py", "js", "ts", "jsx", "tsx", "json", "yml", "yaml", "html", "css", "md", "sql", "xml", "txt"]}
                  size="md"
                />
              </div>
              {uploadedFile && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Selected: <span className="font-medium">{uploadedFile.name}</span>
                  {!modalFileName && (
                    <div className="mt-1">
                      Will use: <span className="font-medium">{uploadedFile.name}</span>
                    </div>
                  )}
                </div>
              )}
              {activeFolder && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  File will be created in: <span className="font-medium">{activeFolder}/</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>

      {/* Project Upload Modal */}
      <Dialog
        isOpen={showProjectUploadModal}
        onClose={() => {
          setShowProjectUploadModal(false);
          setUploadedProject(null);
        }}
        title="Upload Project Folder"
        size="lg"
        actions={
          <div className="flex gap-2">
            <Button
              variant="neutral"
              onClick={() => {
                setShowProjectUploadModal(false);
                setUploadedProject(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={uploadProject}
              isDisabled={!uploadedProject || uploadedProject.length === 0}
            >
              Upload Project
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select a folder containing your project files. This will replace all current files with the uploaded project contents.
            </p>
            <Alert variant="warning">
              <AlertDescription>
                <strong>Warning:</strong> This action will delete all current files and replace them with the uploaded project.
              </AlertDescription>
            </Alert>
          </div>

          <div className="mt-4">
            <FolderUploadField
              onChange={handleProjectUpload}
              size="lg"
            />
          </div>

          {uploadedProject && uploadedProject.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Selected Files ({uploadedProject.length}):</h4>
              <div className="max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-800 rounded border p-2">
                {Array.from(uploadedProject).map((file, index) => (
                  <div
                    key={index}
                    className="text-xs text-gray-600 dark:text-gray-400 py-1"
                  >
                    {file.webkitRelativePath || file.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={showDeleteFolderDialog}
        onClose={() => {
          if (isDeletingFolder) return;
          setShowDeleteFolderDialog(false);
          setFolderToDelete(null);
        }}
        onConfirm={async () => {
          if (!folderToDelete) return;
          setIsDeletingFolder(true);
          try {
            await removeFolder(folderToDelete);
            setShowDeleteFolderDialog(false);
            setFolderToDelete(null);
          } finally {
            setIsDeletingFolder(false);
          }
        }}
        title="Delete Folder"
        message={folderToDelete ? `Are you sure you want to delete the folder "${folderToDelete}"?` : "Are you sure you want to delete this folder?"}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeletingFolder}
        variant="danger"
      >
        <Text className="text-sm text-red-600 dark:text-red-400 font-semibold">All files and subfolders within this folder will also be deleted.</Text>
      </ConfirmDialog>
    </View>
  );
};
